import { useRef } from 'react';
import type { Permission } from '@shared/constants/index.ts';
import { useSession } from '../state/session.tsx';
import { usePlanFeature } from './plan-gate.tsx';

/** Local drafts and delayed responses belong to their original authorization scope. */
export function useChecklistEditScope(permission: Permission) {
  const session = useSession();
  const scheduling = usePlanFeature('scheduling');
  const key = JSON.stringify([
    session.user?.id,
    session.company?.id,
    session.currentLocation?.id,
    session.user?.role,
    session.user?.permissions,
    session.company?.planFeatures,
    session.company?.accessLocked,
    session.sessionConfirmed,
    scheduling,
    session.can(permission),
  ]);
  const current = useRef(key);
  current.current = key;
  const captured = useRef<string | null>(null);
  // First hydration is not a scope change. Capture only a confirmed session;
  // later authorization changes still fence an already opened draft.
  if (captured.current === null && session.sessionConfirmed && session.user && session.company && scheduling)
    captured.current = key;
  return {
    allowed: captured.current === key && scheduling && session.can(permission) && session.sessionConfirmed,
    sameScope: captured.current === null || captured.current === key,
    capture: () => {
      captured.current = current.current;
      return captured.current;
    },
    isCurrent: (stamp: string | null = captured.current) => stamp === current.current,
    stamp: key,
  };
}
